export const CALLS_HELP_SECTIONS = [
  {
    key: 'operation',
    label: 'Operação',
    title: 'Central de Chamadas em tempo real',
    description: 'Use esta tela para acompanhar, assumir, finalizar e reabrir chamadas de mesas sem perder a ordem operacional.',
    highlights: [
      'A lista prioriza chamadas abertas e escalonadas antes de atendimentos em andamento.',
      'A conexão em tempo real precisa estar conectada para refletir novas chamadas imediatamente.',
      'O som de alerta deve ficar ativo em operações de balcão ou salão com atendimento contínuo.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Status da chamada',
        items: [
          'Aberta: a mesa ainda aguarda atendimento.',
          'Em atendimento: alguém assumiu a chamada e deve finalizar antes de iniciar outra quando for garçom.',
          'Finalizada: atendimento concluído e removido da fila operacional.',
          'Escalonada: chamada aberta há tempo suficiente para exigir prioridade.',
        ],
      },
      {
        tone: 'warning',
        title: 'Restaurante fechado',
        items: [
          'Quando o restaurante está fechado, novas chamadas não devem ser geradas pelo painel público.',
          'Se houver chamadas antigas em aberto, revise antes de iniciar uma nova operação.',
        ],
      },
    ],
  },
  {
    key: 'actions',
    label: 'Ações',
    title: 'Assumir, finalizar e reabrir atendimentos',
    description: 'As ações mudam o status operacional da chamada e impactam a fila em tempo real.',
    highlights: [
      'Garçons devem finalizar o atendimento atual antes de assumir outra mesa.',
      'Reabertura deve ser usada apenas quando uma chamada foi finalizada por engano.',
    ],
    blocks: [
      {
        tone: 'success',
        title: 'Fluxo recomendado',
        items: [
          'Assuma a chamada somente quando o atendimento realmente for iniciado.',
          'Finalize a chamada assim que a solicitação da mesa for atendida.',
          'Use Atualizar quando houver suspeita de instabilidade de rede ou reconexão.',
        ],
      },
      {
        tone: 'security',
        title: 'Permissões',
        items: [
          'A reabertura fica restrita a perfis administrativos ou gerenciais.',
          'Usuários do tipo garçom trabalham com trava operacional para evitar múltiplas chamadas assumidas ao mesmo tempo.',
        ],
      },
    ],
  },
];

export const TABLES_HELP_SECTIONS = [
  {
    key: 'registry',
    label: 'Cadastro',
    title: 'Mesas, áreas e status operacional',
    description: 'Use esta tela para manter as mesas do restaurante organizadas antes de gerar QR Codes para atendimento.',
    highlights: [
      'Cada mesa precisa ter uma identificação clara para a equipe reconhecer a origem da chamada.',
      'Áreas ajudam a separar salão, varanda, balcão ou outros setores operacionais.',
      'Mesas inativas não devem ser usadas para novos QR Codes em operação.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Identificação da mesa',
        items: [
          'Use nomes ou números iguais aos utilizados fisicamente no restaurante.',
          'Evite identificações duplicadas ou ambíguas, como “Mesa 1” em áreas diferentes sem informar a área.',
          'Atualize a área quando a disposição do salão mudar para manter a fila de chamadas confiável.',
        ],
      },
      {
        tone: 'warning',
        title: 'Ativação e histórico',
        items: [
          'Inativar uma mesa preserva o histórico operacional e evita perda de rastreabilidade.',
          'Excluir deve ser reservado para cadastros criados por engano ou que não possuem uso operacional relevante.',
        ],
      },
    ],
  },
  {
    key: 'qrcode',
    label: 'QR Code',
    title: 'Geração e uso do QR Code',
    description: 'O QR Code identifica a mesa e permite que o cliente abra chamadas sem instalar aplicativo.',
    highlights: [
      'Gere o QR Code após confirmar restaurante, mesa, área e status ativo.',
      'O link copiado deve ser tratado como endereço público da mesa.',
      'Se a mesa for recriada, gere e imprima um novo QR Code.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Antes de imprimir',
        items: [
          'Confira se o QR Code pertence ao restaurante correto.',
          'Valide a identificação da mesa na pré-visualização antes de baixar ou imprimir.',
          'Teste a leitura com um celular antes de distribuir o material no salão.',
        ],
      },
      {
        tone: 'info',
        title: 'Descrição opcional',
        items: [
          'Use a descrição para orientar o cliente, por exemplo “Escaneie para chamar o garçom”.',
          'Ajuste o tamanho do texto conforme o espaço físico do material impresso.',
        ],
      },
    ],
  },
  {
    key: 'printing',
    label: 'Impressão',
    title: 'Impressão individual e em massa',
    description: 'A impressão em massa ajuda a preparar várias mesas com o mesmo padrão visual e reduzir erro manual.',
    highlights: [
      'Use o preset de impressão conforme o tamanho do material físico.',
      'Filtre por área quando for preparar QR Codes por setor do restaurante.',
      'Baixe o ZIP quando precisar enviar os arquivos para gráfica ou impressão externa.',
    ],
    blocks: [
      {
        tone: 'success',
        title: 'Preset recomendado',
        items: [
          'Balcão prioriza leitura maior e menos itens por página.',
          'Mesa equilibra tamanho de QR Code e quantidade por página.',
          'Compacto economiza espaço, mas exige cuidado para manter boa leitura na impressão.',
        ],
      },
      {
        tone: 'warning',
        title: 'Cuidados de operação',
        items: [
          'Não misture QR Codes de restaurantes diferentes na mesma impressão.',
          'Substitua o impresso quando a mesa mudar de identificação ou área.',
          'Evite plastificação ou suporte físico que gere reflexo forte sobre o QR Code.',
        ],
      },
    ],
  },
];

export const RESTAURANTS_HELP_SECTIONS = [
  {
    key: 'registry',
    label: 'Cadastro',
    title: 'Restaurantes e unidade operacional',
    description: 'Use esta tela para cadastrar unidades, controlar o restaurante padrão e manter a base operacional consistente.',
    highlights: [
      'O restaurante ativo define onde mesas, chamadas e equipe serão vinculadas.',
      'O restaurante padrão facilita a navegação e reduz seleção manual no dia a dia.',
      'Restaurantes inativos devem ser mantidos apenas quando ainda houver histórico relevante.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Dados principais',
        items: [
          'Informe um nome reconhecível para a operação e para a equipe.',
          'Use o código interno quando existir identificação da unidade em outro sistema.',
          'Revise o fuso horário antes de habilitar regras de funcionamento.',
        ],
      },
      {
        tone: 'security',
        title: 'Restaurante padrão',
        items: [
          'Defina como padrão a unidade mais usada pela empresa.',
          'Trocar o padrão não move mesas ou chamadas entre restaurantes.',
          'Usuários ainda precisam ter vínculo com o restaurante correto para operar.',
        ],
      },
    ],
  },
  {
    key: 'hours',
    label: 'Horários',
    title: 'Horário de funcionamento',
    description: 'A regra de horário controla quando clientes e integrações podem gerar chamadas para o restaurante.',
    highlights: [
      'Com horário habilitado, chamadas fora do período configurado são bloqueadas no QR Code e nas integrações.',
      'Turnos quebrados permitem cobrir almoço e jantar no mesmo dia.',
      'Exceções de datas ajudam a tratar feriados, eventos e fechamentos pontuais.',
    ],
    blocks: [
      {
        tone: 'warning',
        title: 'Impacto no atendimento',
        items: [
          'Não habilite horário de funcionamento sem cadastrar os turnos necessários.',
          'Se o restaurante opera após meia-noite, revise cuidadosamente como os turnos foram divididos.',
          'Teste uma chamada pelo QR Code após salvar mudanças relevantes de horário.',
        ],
      },
      {
        tone: 'info',
        title: 'Fuso horário',
        items: [
          'O fuso define como o sistema interpreta abertura, fechamento e exceções.',
          'Para operações no Brasil, mantenha o fuso correspondente à cidade da unidade.',
          'Fuso incorreto pode bloquear chamadas em horários aparentemente válidos para a equipe.',
        ],
      },
    ],
  },
  {
    key: 'maintenance',
    label: 'Manutenção',
    title: 'Edição, inativação e consistência',
    description: 'Mudanças em restaurante afetam mesas, equipe, QR Codes e integrações vinculadas à unidade.',
    highlights: [
      'Prefira inativar uma unidade em vez de excluir quando ela já possui histórico.',
      'Revise vínculos de equipe e credenciais ao alterar a estrutura de restaurantes.',
      'Após mudanças críticas, peça para a equipe atualizar a tela ou trocar o restaurante ativo.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Antes de alterar',
        items: [
          'Confirme se está editando a empresa e o restaurante corretos.',
          'Valide se há mesas ativas vinculadas antes de arquivar a unidade.',
          'Revise integrações com escopo por restaurante para evitar chamadas em unidade errada.',
        ],
      },
    ],
  },
];

export const AREAS_HELP_SECTIONS = [
  {
    key: 'registry',
    label: 'Cadastro',
    title: 'Áreas organizam a operação do salão',
    description: 'Use áreas para agrupar mesas por setor, facilitando a leitura da fila e a distribuição do atendimento.',
    highlights: [
      'A área deve refletir a organização física usada pela equipe.',
      'Mesas vinculadas à área aparecem com esse contexto nas chamadas.',
      'Áreas inativas preservam histórico e evitam uso em novos cadastros.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Quando criar áreas',
        items: [
          'Crie áreas como salão, varanda, balcão, externo ou piso superior quando isso ajudar a equipe a localizar a mesa.',
          'Use nomes curtos e reconhecíveis para reduzir erro durante atendimento.',
          'Revise áreas antes de imprimir QR Codes das mesas.',
        ],
      },
      {
        tone: 'warning',
        title: 'Cuidados de manutenção',
        items: [
          'Evite excluir áreas que já possuem mesas ou histórico operacional.',
          'Prefira inativar áreas que saíram da operação temporariamente.',
          'Ao reorganizar salão, revise também o cadastro das mesas vinculadas.',
        ],
      },
    ],
  },
];

export const COMPANIES_HELP_SECTIONS = [
  {
    key: 'registry',
    label: 'Cadastro',
    title: 'Empresas controlam escopo, restaurantes e equipe',
    description: 'Esta tela é administrativa e deve ser usada com cuidado, porque alterações de empresa afetam usuários, restaurantes, mesas e assinatura.',
    highlights: [
      'Documento, responsável e contato ajudam na rastreabilidade administrativa.',
      'Empresas inativas não devem ser usadas na operação diária.',
      'Excluir empresa remove estrutura operacional vinculada, conforme regra do backend.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Antes de criar ou alterar',
        items: [
          'Confirme se a empresa realmente precisa ser separada de outras operações.',
          'Revise documento e responsável antes de salvar, pois esses dados orientam suporte e administração.',
          'Após criar empresa, revise restaurantes, equipe e permissões antes de liberar uso.',
        ],
      },
      {
        tone: 'warning',
        title: 'Exclusão e inativação',
        items: [
          'Evite excluir empresas com histórico operacional.',
          'Use inativação quando a empresa deve sair da rotina sem perda de rastreabilidade.',
          'Excluir pode impactar restaurantes, equipe, mesas e chamadas vinculadas.',
        ],
      },
    ],
  },
];

export const INTEGRATIONS_HELP_SECTIONS = [
  {
    key: 'credentials',
    label: 'Credenciais',
    title: 'Credenciais HMAC para integrações',
    description: 'Use credenciais de integração para permitir que sistemas externos criem ou consultem chamadas com controle e auditoria.',
    highlights: [
      'O segredo da credencial é exibido apenas no momento da criação ou rotação.',
      'Credenciais antigas devem ser revogadas quando houver troca de fornecedor ou suspeita de exposição.',
      'Defina expiração quando a integração for temporária ou usada em homologação.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Segurança da chave',
        items: [
          'Nunca envie o segredo por canais informais ou tickets públicos.',
          'Use nomes claros para identificar sistema, fornecedor e finalidade da credencial.',
          'Prefira rotacionar a credencial antes de revogar quando precisar manter uma migração sem interrupção.',
        ],
      },
      {
        tone: 'warning',
        title: 'Escopo por restaurante',
        items: [
          'Vincule a credencial apenas aos restaurantes que realmente precisam receber chamadas.',
          'Credenciais sem restaurante específico podem operar para todos os restaurantes permitidos da empresa.',
        ],
      },
    ],
  },
  {
    key: 'signature',
    label: 'Assinatura',
    title: 'Como assinar as requisições',
    description: 'A API exige assinatura HMAC para reduzir risco de replay, adulteração de corpo e uso indevido de chave.',
    highlights: [
      'A assinatura usa método HTTP, PATH com query string, timestamp e corpo bruto.',
      'O mesmo PATH+QUERY usado na URL deve ser usado na mensagem assinada.',
      'O nonce evita repetição de requisições capturadas.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Headers obrigatórios',
        items: [
          'X-Api-Key identifica a credencial.',
          'X-Timestamp deve representar o horário da requisição.',
          'X-Nonce deve ser único por requisição.',
          'X-Signature contém o HMAC calculado com o segredo.',
        ],
      },
      {
        tone: 'warning',
        title: 'Erros comuns',
        items: [
          'Assinar uma query diferente da URL chamada causa falha de autenticação.',
          'Serializar o JSON de forma diferente entre assinatura e envio altera o corpo bruto.',
          'Relógio muito fora do horário esperado pode invalidar a requisição.',
        ],
      },
    ],
  },
];

export const STAFF_HELP_SECTIONS = [
  {
    key: 'roles',
    label: 'Funções',
    title: 'Equipe, funções e permissões',
    description: 'Use a tela Equipe para cadastrar colaboradores e controlar o que cada pessoa pode operar no WaiterCall.',
    highlights: [
      'Garçons devem ser vinculados aos restaurantes onde atuarão.',
      'Gerentes têm permissão operacional maior, mas não devem editar administradores.',
      'Inativar usuário é mais seguro que excluir quando há histórico operacional relevante.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Hierarquia de acesso',
        items: [
          'Admin gerencia a operação da empresa.',
          'Gerente gerencia restaurante e equipe operacional.',
          'Garçom atua no atendimento das chamadas.',
        ],
      },
      {
        tone: 'warning',
        title: 'Senha e acesso',
        items: [
          'Na criação, informe uma senha inicial segura para o colaborador.',
          'Na edição, deixe o campo de senha vazio quando não quiser alterá-la.',
          'Evite compartilhar usuários entre pessoas diferentes para manter rastreabilidade.',
        ],
      },
    ],
  },
  {
    key: 'presence',
    label: 'Presença',
    title: 'Presença em tempo real e vínculo com restaurante',
    description: 'A presença ajuda a entender quem está online e quem tem vínculo com o restaurante selecionado.',
    highlights: [
      'Sem vínculo indica que o usuário não está associado ao restaurante ativo.',
      'Presença offline pode indicar sessão encerrada, perda de conexão ou usuário fora da operação.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Como interpretar',
        items: [
          'Online: usuário conectado no restaurante selecionado.',
          'Offline: usuário tem vínculo, mas não está conectado no momento.',
          'Sem vínculo: usuário existe na empresa, mas não atua no restaurante ativo.',
        ],
      },
    ],
  },
];

export const MY_SUBSCRIPTION_HELP_SECTIONS = [
  {
    key: 'overview',
    label: 'Visão geral',
    title: 'Plano, cobrança e consumo',
    description: 'Esta tela mostra a assinatura da empresa ativa e os limites contratados para a operação do WaiterCall.',
    highlights: [
      'A assinatura exibida pertence à empresa ativa no seletor lateral.',
      'Limites de mesas e restaurantes ajudam a evitar expansão sem cobertura do plano.',
      'Status de cobrança pode restringir acesso conforme a regra comercial.',
    ],
    blocks: [
      {
        tone: 'info',
        title: 'Consumo do plano',
        items: [
          'Mesas ativas representam o volume operacional cadastrado.',
          'Restaurantes ativos representam unidades que consomem o plano.',
          'Quando o consumo se aproxima do limite, avalie troca de plano antes de bloquear expansão.',
        ],
      },
      {
        tone: 'warning',
        title: 'Status de cobrança',
        items: [
          'Em atraso indica cobrança vencida e risco de restrição.',
          'Suspensa indica que o acesso pode estar bloqueado até regularização.',
          'Cancelada indica encerramento da assinatura e retenção temporária de dados.',
        ],
      },
    ],
  },
  {
    key: 'changes',
    label: 'Alterações',
    title: 'Troca de plano e cancelamento',
    description: 'Mudanças na assinatura são sensíveis porque afetam acesso, limites e cobrança.',
    highlights: [
      'Troca de plano entra em vigor imediatamente quando confirmada.',
      'Durante avaliação gratuita, o cancelamento não gera cobrança.',
      'Fora da avaliação, revise impacto comercial antes de cancelar.',
    ],
    blocks: [
      {
        tone: 'security',
        title: 'Antes de alterar',
        items: [
          'Confirme se a empresa ativa é a empresa correta.',
          'Valide se o novo plano cobre a quantidade atual de mesas e restaurantes.',
          'Registre o motivo internamente quando houver solicitação feita por cliente.',
        ],
      },
    ],
  },
];
